import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Chip, Alert, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import FileUploader from '../components/FileUploader';
import ThemeLoader from '../components/ThemeLoader';
import JobDashboard from '../components/JobDashboard';
import WorkflowForm from '../components/WorkflowForm';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { generatePrefixedJobName, generateJobName } from '../utils/jobNaming';

interface DocumentAnalysis {
  files: string[];
  collectionType: string;
  confidence: string;
  fileCount: number;
  s3Uris?: string[];
  recommendedWorkflows: Array<{
    practice: string;
    workflow: string;
    reason: string;
  }>;
}

export const HomePage: React.FC = () => {
  // Feature flags
  const smartAnalysisEnabled = process.env.REACT_APP_ENABLE_SMART_ANALYSIS === 'true';
  
  const [files, setFiles] = useState<File[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DocumentAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'smart' | 'manual'>(smartAnalysisEnabled ? 'smart' : 'manual');

  const navigate = useNavigate();

  const analyzeDocuments = async () => {
    if (files.length === 0) return;

    setAnalyzing(true);
    setError(null);
    
    try {
      // Upload files first
      const uploadResult = await apiService.uploadFiles(files, 'general', generatePrefixedJobName('ANALYSIS'));
      
      // Analyze all files together
      const fileData = files.map((file, index) => ({
        fileName: file.name,
        s3Uri: uploadResult.s3Uris[index]
      }));
      
      const result = await apiService.analyzeDocuments(fileData);
      setAnalysis({ ...result, s3Uris: uploadResult.s3Uris });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const startWorkflow = async (analysis: DocumentAnalysis, recommendation: any) => {
    try {
      if (!analysis.s3Uris || analysis.s3Uris.length === 0) {
        throw new Error('Files not available for workflow execution');
      }
      
      // Execute the workflow with all uploaded files
      const executionResult = await apiService.executeWorkflow({
        practice: recommendation.practice,
        workflows: [recommendation.workflow],
        number: generateJobName(),
        input_file_s3_uris: analysis.s3Uris
      });
      
      // Job will appear in the dashboard automatically
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start workflow');
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Column - Job Creation */}
        <Grid item xs={12} md={4}>
          <Typography variant="h4" gutterBottom>
            Create New Job
          </Typography>
          
          <Card sx={{ mb: 3 }}>
            <CardContent>
              {/* File Upload Area */}
              <FileUploader 
                files={files} 
                setFiles={setFiles}
                showNextButton={false}
              />
              
              {files.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  {/* Mode Toggle - only show if smart analysis is enabled */}
                  {smartAnalysisEnabled && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <ToggleButtonGroup
                        value={mode}
                        exclusive
                        onChange={(_, newMode) => newMode && setMode(newMode)}
                        size="small"
                      >
                        <ToggleButton value="smart">
                          🤖 Smart Analysis
                        </ToggleButton>
                        <ToggleButton value="manual">
                          ⚙️ Manual Selection
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                  
                  {mode === 'smart' && smartAnalysisEnabled ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        Let AI analyze your documents and recommend workflows
                      </Typography>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Button 
                          variant="contained" 
                          onClick={analyzeDocuments}
                          disabled={analyzing}
                          size="large"
                        >
                          {analyzing ? 'Analyzing Documents...' : `Analyze ${files.length} Document${files.length > 1 ? 's' : ''}`}
                        </Button>
                      </Box>
                      
                      {analyzing && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <ThemeLoader />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            AI is analyzing your documents...
                          </Typography>
                        </Box>
                      )}
                      
                      {analysis && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            📋 Analysis Results
                          </Typography>
                          
                          <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {analysis.fileCount} Document{analysis.fileCount > 1 ? 's' : ''} Collection
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Files: {analysis.files.join(', ')}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Chip label={analysis.collectionType} color="primary" size="small" />
                                    <Chip label={`${analysis.confidence} confidence`} variant="outlined" size="small" />
                                  </Box>
                                </Box>
                              </Box>
                              
                              <Typography variant="body2" fontWeight="bold" sx={{ mb: 2 }}>
                                Recommended workflows for all {analysis.fileCount} files:
                              </Typography>
                              
                              {analysis.recommendedWorkflows.map((rec, recIndex) => (
                                <Box key={recIndex} sx={{ 
                                  border: 1, 
                                  borderColor: 'divider', 
                                  borderRadius: 1, 
                                  p: 2, 
                                  mb: 1,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start'
                                }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {rec.practice} → {rec.workflow}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {rec.reason}
                                    </Typography>
                                  </Box>
                                  <Button 
                                    variant="contained" 
                                    size="small"
                                    onClick={() => startWorkflow(analysis, rec)}
                                    sx={{ flexShrink: 0, minHeight: '32px' }}
                                  >
                                    Start Job
                                  </Button>
                                </Box>
                              ))}
                            </CardContent>
                          </Card>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        Choose a specific practice and workflow for your documents
                      </Typography>
                      
                      <WorkflowForm 
                        files={files} 
                        onExecute={() => {
                          setFiles([]);
                          setAnalysis(null);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Grid>
        
        {/* Right Column - Job Queue */}
        <Grid item xs={12} md={8}>
          <Typography variant="h4" gutterBottom>
            Job Queue
          </Typography>
          <JobDashboard />
        </Grid>
      </Grid>
    </Box>
  );
};

export default HomePage;